import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { REGIONS, REGION_BY_ID, packageById } from '@/pages/demo/revenue/components/revenueData';
import StatusBadge from './StatusBadge';
import LeadBoard from './LeadBoard';
import LeadDetail from './LeadDetail';
import {
  LEADS,
  LEAD_SOURCES,
  LEAD_STAGES,
  OWNERS,
  STAGE_TONE,
  isOpen,
  leadValueLabel,
  pipelineValue,
  formatDate,
  regionLabels,
} from './sponsorData';
import type { Lead, LeadStage, LeadSource } from './sponsorData';

type View = 'list' | 'board';
type StageFilter = 'all' | 'open' | LeadStage;

const clone = (list: Lead[]): Lead[] =>
  list.map((l) => ({ ...l, regionIds: [...l.regionIds], activity: [...l.activity], tasks: l.tasks.map((t) => ({ ...t })) }));

const selectClass = 'text-sm border border-background-300 rounded-md px-3 py-2 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer';

function Kpi({ label, value, note, icon, accent = false }: { label: string; value: string; note: string; icon: string; accent?: boolean }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-500">{label}</p>
        <span className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${accent ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-600'}`}><i className={icon}></i></span>
      </div>
      <p className="mt-2.5 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-none">{value}</p>
      <p className="mt-1.5 text-[11px] text-foreground-500">{note}</p>
    </div>
  );
}

export default function SponsorsLeads() {
  const [leads, setLeads] = useState<Lead[]>(() => clone(LEADS));
  const [selectedId, setSelectedId] = useState<string | null>(LEADS[0].id);
  const [view, setView] = useState<View>('list');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | LeadSource>('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [flash, setFlash] = useState('');

  const update = (id: string, fn: (l: Lead) => Lead) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? fn(l) : l)));
  };

  const setFlashMsg = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(''), 3600);
  };

  const selected = leads.find((l) => l.id === selectedId) ?? null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (stageFilter === 'open' && !isOpen(l)) return false;
      if (stageFilter !== 'all' && stageFilter !== 'open' && l.stage !== stageFilter) return false;
      if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
      if (ownerFilter !== 'all' && l.owner !== ownerFilter) return false;
      if (regionFilter !== 'all' && !l.regionIds.includes(regionFilter)) return false;
      if (q && !(`${l.company} ${l.contact} ${l.email}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [leads, stageFilter, sourceFilter, ownerFilter, regionFilter, search]);

  const openLeads = leads.filter(isOpen);
  const won = leads.filter((l) => l.stage === 'Won');
  const lost = leads.filter((l) => l.stage === 'Lost');
  const winRate = won.length + lost.length > 0 ? Math.round((won.length / (won.length + lost.length)) * 100) : 0;

  const handleSetStage = (id: string, stage: LeadStage) => {
    update(id, (l) => ({
      ...l,
      stage,
      lastTouch: '2026-10-09',
      activity: [{ id: `a-${Date.now()}`, kind: 'stage', text: `Stage moved to “${stage}”`, at: '2026-10-09' }, ...l.activity],
    }));
    setFlashMsg(`Stage updated to “${stage}”. Preview only — nothing is saved.`);
  };

  const handleToggleRegion = (id: string, regionId: string) => {
    update(id, (l) => ({
      ...l,
      regionIds: l.regionIds.includes(regionId) ? l.regionIds.filter((r) => r !== regionId) : [...l.regionIds, regionId],
    }));
  };

  const handleSelectPackage = (id: string, packageId: string | null) => {
    const pkg = packageId ? packageById(packageId) : undefined;
    update(id, (l) => ({ ...l, packageId, valueMonthly: pkg ? pkg.price : null }));
  };

  const handleAddNote = (id: string, text: string) => {
    update(id, (l) => ({
      ...l,
      lastTouch: '2026-10-09',
      activity: [{ id: `a-${Date.now()}`, kind: 'note', text, at: '2026-10-09' }, ...l.activity],
    }));
    setFlashMsg('Note added — kept in this preview only.');
  };

  const handleToggleTask = (id: string, taskId: string) => {
    update(id, (l) => ({ ...l, tasks: l.tasks.map((t) => (t.id === taskId ? { ...t, done: !t.done } : t)) }));
  };

  const handleAddTask = (id: string, label: string, due: string) => {
    update(id, (l) => ({ ...l, tasks: [...l.tasks, { id: `t-${Date.now()}`, label, due, done: false }] }));
    setFlashMsg('Follow-up task added — preview only.');
  };

  const handleConvert = (id: string) => {
    update(id, (l) => ({
      ...l,
      stage: 'Won',
      lastTouch: '2026-10-09',
      activity: [{ id: `a-${Date.now()}`, kind: 'stage', text: `Converted to an active ${packageById(l.packageId ?? '')?.name ?? 'sponsorship'} campaign`, at: '2026-10-09' }, ...l.activity],
    }));
    setFlashMsg('Lead converted to a campaign (simulated) — no real campaign is created.');
  };

  const reset = () => {
    setLeads(clone(LEADS));
    setSelectedId(LEADS[0].id);
    setFlashMsg('Sample leads restored.');
  };

  const stageOptions: { id: StageFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'open', label: 'Open' },
    ...LEAD_STAGES.map((s) => ({ id: s as StageFilter, label: s })),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Sponsors &amp; Leads</h2>
        <DemoBadge label="Sample data — nothing saved" />
      </div>

      <div className="flex items-start gap-3 bg-accent-50 border border-accent-300 border-l-4 border-l-accent-500 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
        <p className="text-sm text-foreground-800">
          <strong className="font-semibold">Sponsor CRM preview.</strong> Track enquiries, move deals through the pipeline, log notes and tasks, and convert a won lead into a campaign. Every change here is local to this screen — nothing is written to the Backend.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Open leads" value={String(openLeads.length)} note="Active opportunities" icon="ri-user-search-line" />
        <Kpi label="Pipeline value" value={`$${pipelineValue(leads).toLocaleString('en-US')}`} note="Open monthly value" icon="ri-money-dollar-circle-line" accent />
        <Kpi label="Won" value={String(won.length)} note="Converted sponsors" icon="ri-checkbox-circle-line" />
        <Kpi label="Win rate" value={`${winRate}%`} note={`${lost.length} lost`} icon="ri-percent-line" accent />
      </div>

      {/* Controls */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400"><i className="ri-search-line"></i></span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, contact or email…"
              className="w-full text-sm border border-background-300 rounded-md pl-9 pr-3 py-2 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as 'all' | LeadSource)} className={selectClass} aria-label="Filter by source">
              <option value="all">All sources</option>
              {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className={selectClass} aria-label="Filter by owner">
              <option value="all">All owners</option>
              {OWNERS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className={selectClass} aria-label="Filter by region">
              <option value="all">All regions</option>
              {REGIONS.map((r) => <option key={r.id} value={r.id}>{r.city}</option>)}
            </select>
            <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
              <button type="button" onClick={() => setView('list')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${view === 'list' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check"></i></span>List
              </button>
              <button type="button" onClick={() => setView('board')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${view === 'board' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-kanban-view"></i></span>Pipeline
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {stageOptions.map((o) => {
            const on = stageFilter === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => setStageFilter(o.id)}
                className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}
              >
                {o.label}
              </button>
            );
          })}
          <button type="button" onClick={reset} className="ml-auto inline-flex items-center gap-1.5 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-restart-line"></i></span>Reset
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className={selected ? 'xl:col-span-7' : 'xl:col-span-12'}>
          {view === 'board' ? (
            <LeadBoard leads={filtered} selectedId={selectedId} onSelect={setSelectedId} onSetStage={handleSetStage} />
          ) : (
            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-background-200 font-label uppercase tracking-widest text-[10px] text-foreground-500">
                <div className="col-span-4">Company / contact</div>
                <div className="col-span-2">Stage</div>
                <div className="col-span-2">Value</div>
                <div className="col-span-2">Regions</div>
                <div className="col-span-2 text-right">Next step</div>
              </div>
              <ul className="divide-y divide-background-200">
                {filtered.map((l) => {
                  const on = l.id === selectedId;
                  return (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(l.id)}
                        className={`w-full text-left px-4 py-3.5 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center transition-colors cursor-pointer ${on ? 'bg-primary-50' : 'hover:bg-background-100'}`}
                      >
                        <div className="md:col-span-4 min-w-0">
                          <p className="text-sm font-medium text-foreground-950 truncate">{l.company}</p>
                          <p className="text-xs text-foreground-500 truncate">{l.contact} · {l.source}</p>
                        </div>
                        <div className="md:col-span-2"><StatusBadge tone={STAGE_TONE[l.stage]}>{l.stage}</StatusBadge></div>
                        <div className="md:col-span-2 text-sm text-foreground-800 font-medium">{leadValueLabel(l)}</div>
                        <div className="md:col-span-2 text-xs text-foreground-600 truncate">{regionLabels(l.regionIds, REGION_BY_ID)}</div>
                        <div className="md:col-span-2 text-xs text-foreground-600 md:text-right whitespace-nowrap">{l.nextStep ? formatDate(l.nextStep) : '—'}</div>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && <li className="px-4 py-8 text-sm text-foreground-500 text-center">No leads match these filters.</li>}
              </ul>
            </div>
          )}
          <p className="mt-2.5 text-xs text-foreground-500">{filtered.length} of {leads.length} leads shown.</p>
        </div>

        {selected && (
          <div className="xl:col-span-5">
            <LeadDetail
              lead={selected}
              flash={flash}
              onClose={() => setSelectedId(null)}
              onSelectStage={(stage) => handleSetStage(selected.id, stage)}
              onToggleRegion={(regionId) => handleToggleRegion(selected.id, regionId)}
              onSelectPackage={(packageId) => handleSelectPackage(selected.id, packageId)}
              onAddNote={(text) => handleAddNote(selected.id, text)}
              onToggleTask={(taskId) => handleToggleTask(selected.id, taskId)}
              onAddTask={(label, due) => handleAddTask(selected.id, label, due)}
              onConvert={() => handleConvert(selected.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}